import axios, { AxiosRequestConfig } from "axios";
import { addBtmIdInterceptor } from "@sazka/utils/http-clients/btm-id-interceptor";
import { addSagAuthInterceptor } from "@sazka/utils/http-clients/sag-auth-interceptor";
import { useSagOptions } from "@sazka/utils/http-clients/use-sag-options";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import { addSessionTokenInterceptor } from "../../interceptors/session-token-interceptor";
import { useNgSession } from "../../ng-session";
import { PlayerAccountResponse } from "../model/player-account-response";
import {
    SelfExclusionRequest,
    SelfExclusionGetResponse,
    SelfExclusionPostResponse,
} from "../model/self-exclusion-model";

const { getSagApiUrl } = useSagOptions();
const { session } = useNgSession();

/**
 * Axios basic configuration
 * Some general configuration can be added like timeout, headers, params etc. More details can be found on https://github.com/axios/axios
 * */
const config: AxiosRequestConfig = {
    baseURL: "",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "X-Platform-Type": ResponsiveUtils.isMobile() ? "C" : "W" },
};

const playerAccountHttpClient = axios.create(config);

/** Adding the request interceptors */
addSagAuthInterceptor(playerAccountHttpClient);
addBtmIdInterceptor(playerAccountHttpClient);
addSessionTokenInterceptor(playerAccountHttpClient);

const getPlayerAccount = async () => {
    if (!session.value?.PlayerID) throw new Error("Session PlayerID is missing");

    const response = await playerAccountHttpClient.get<PlayerAccountResponse>(
        `${getSagApiUrl("TempAccount")}GetPlayerDetails`,
        {
            params: { playerID: session.value.PlayerID },
        }
    );

    return response.data;
};

const getPlayerSelfExclusion = async () => {
    if (!session.value?.PlayerID) throw new Error("Session PlayerID is missing");

    const response = await playerAccountHttpClient.get<SelfExclusionGetResponse>(
        `${getSagApiUrl("SelfExclusion")}player/${session.value.PlayerID}/getplayerselfexclusion`
    );

    return response.data;
};

const setPlayerSelfExclusion = async (
    dataVersion: string,
    unit: string,
    length: number,
    pin: string,
    typeId: number
) => {
    if (!session.value?.PlayerID) throw new Error("Session PlayerID is missing");

    const request: SelfExclusionRequest = {
        DataVersions: dataVersion,
        Duration: length,
        DurationUnit: unit,
        SelfExclusionTypeId: typeId,
        SelfExclusionScope: "N",
        Challenges: {
            OneTimePassword: pin,
        },
    };

    const response = await playerAccountHttpClient.post<SelfExclusionPostResponse>(
        `${getSagApiUrl("SelfExclusion")}player/${session.value.PlayerID}/setplayerselfexclusion`,
        request
    );

    return response.data;
};

export { getPlayerAccount, getPlayerSelfExclusion, setPlayerSelfExclusion };
